import EarthIcon from "@/assets/earth-icon.svg?react"
import LeftArrowIcon from "@/assets/left-arrow-icon.svg?react"
import Logo from "@/assets/logo.svg?react"
import UserProfileIcon from "@/assets/user-profile-icon.svg?react"
import {
  useChangeLanguageMutation,
  useContractInfoQuery,
} from "@/entities/account/account.queries"
import { I18nEnum, UserLanguageEnum } from "@/entities/account/account.types"
import { useIsStaffAccount } from "@/entities/assistant/assistant.access"
import { useAuthStore } from "@/entities/auth/auth.model"
import {
  getTierName,
  getWhetherUserIsOtc,
  getWhetherUserIsUnlimited,
} from "@/entities/subscription/subscription.constants"
import { useUsageInfoQuery } from "@/entities/subscription/subscription.queries"
import { useHasAssignedCatWorkspacesQuery } from "@/entities/translation/translation.queries"
import { TranslationModeEnum } from "@/entities/translation/translation.types"
import { NotificationPopover } from "@/features/notification/notification-popover"
import { QualityEvaluationBadge } from "@/features/notification/quality-evaluation-badge"
import ThemeToggle from "@/features/theme/theme-toggle"
import WorkspaceSwitcher from "@/features/workspace/workspace-switcher"
import i18n from "@/shared/lib/react-i18next"
import OveruseTooltip from "@/shared/ui/tooltips/overuse-tooltip"
import UsageWarningTooltip from "@/shared/ui/tooltips/usage-warning-tooltip"
import {
  Button,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
  Spinner,
} from "@beringlab/ui"
import { Link, useRouter, useRouterState } from "@tanstack/react-router"
import { Menu } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import UsageDisplay from "@/features/mt/usage-display"
import { getActiveNavPath } from "./global-nav-bar.lib"

const GlobalNavBar = ({
  hideProfile = false,
  showPreviousButton = false,
  isApiMode = false,
  handleApiButton,
  hideNavigationTab = false,
}: {
  hideProfile?: boolean
  showPreviousButton?: boolean
  isApiMode?: boolean
  handleApiButton?: () => void
  hideNavigationTab?: boolean
}) => {
  const router = useRouter()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false)
  const [isProfileOpen, setIsProfileOpen] = useState<boolean>(false)

  useEffect(() => {
    const media = window.matchMedia("(min-width: 1280px)")
    const closeMenu = (event: MediaQueryListEvent) => {
      if (event.matches) setIsMobileMenuOpen(false)
    }
    media.addEventListener("change", closeMenu)
    return () => media.removeEventListener("change", closeMenu)
  }, [])
  const { user, logout, isAuthenticated } = useAuthStore()
  const { isStaff } = useIsStaffAccount()
  const { data: hasAssignedCatWorkspaces = false } =
    useHasAssignedCatWorkspacesQuery({
      enabled: isAuthenticated,
    })
  const { t } = useTranslation()
  // The back office is reached from the workspace switcher beside this menu, not from inside it.
  const userNavigation = [
    { name: t("settings.setting"), href: "/settings/account" },
  ]
  const { data: contractInfo } = useContractInfoQuery({})
  const { data: accumulatedCount, isPending: isUsageInfoPending } =
    useUsageInfoQuery({ enabled: !!user })
  const isUnlimitedUser = contractInfo
    ? getWhetherUserIsUnlimited(contractInfo)
    : false
  const isUltimatePlan = contractInfo
    ? getTierName(Number(contractInfo.quota)) === "Ultimate"
    : false
  const hidePlanButton = contractInfo
    ? getWhetherUserIsOtc(contractInfo)
    : false
  const isOnTranslationsPage = pathname === "/translations"

  const isKoreanLanguage = i18n.language.startsWith(I18nEnum.Ko)
  const isEnglishLanguage = i18n.language.startsWith(I18nEnum.En)
  // 디버깅용 콘솔 로그는 제거 (불필요)
  const i18nNavigation = [
    { name: "한국어", value: UserLanguageEnum.Ko },
    { name: "English", value: UserLanguageEnum.En },
    // TODO: 일본어 추가시 주석해제
    // { name: "日本語", value: UserLanguageEnum.Ja },
  ]

  const { mutate: changeLanguage } = useChangeLanguageMutation({})

  const handleChangeLanguage = (language: UserLanguageEnum) => {
    changeLanguage({
      language,
    })
  }

  // UserLanguageEnum을 I18nEnum으로 변환하는 함수
  const convertToI18nLanguage = (userLanguage: UserLanguageEnum): string => {
    switch (userLanguage) {
      case UserLanguageEnum.Ko:
        return I18nEnum.Ko
      case UserLanguageEnum.En:
        return I18nEnum.En
      case UserLanguageEnum.Ja:
        return I18nEnum.Ja
      default:
        return I18nEnum.En
    }
  }
  const quota = contractInfo ? Number(contractInfo?.quota) : 5_000
  const used = Number(accumulatedCount) || 0
  const percent = quota ? Math.min((used / quota) * 100, 100) : 0
  const isOverEighty = percent >= 80
  const isOverused = percent >= 100

  // 네비게이션 링크 설정
  const navLinks = [
    {
      path: "/translations",
      text: t("mt.title"),
      search: { mode: TranslationModeEnum.Text },
    },
    ...(isStaff
      ? [{ path: "/translations/cat", text: t("mt.ownedCatNav") }]
      : []),
    ...(hasAssignedCatWorkspaces
      ? [
          {
            path: "/translations/assigned",
            text: t("mt.assignedCatWorkspacesNav"),
          },
        ]
      : []),
    {
      path: "/translations/glossary",
      text: t("mt.glossary"),
    },
    {
      path: "/orders",
      text: t("mt.orders"),
    },
    // Internal tool. Spread conditionally so the tab is absent rather than disabled — the server
    // refuses the requests too, so this only decides whether the entry is offered.
    ...(isStaff
      ? [
          {
            path: "/assistant",
            text: t("assistant.title"),
            alsoActiveOn: ["/reviews"],
          },
          // 번역 검토 is reached from inside 품질 평가 rather than from here. The two are one
          // job — judging a translation — and two neighbouring entries made them read as
          // unrelated tools. Its routes are unchanged, so a shared /reviews link still opens.
          // Its own entry rather than a link to the one tool inside it: these are a category —
          // self-contained things that take a file and give one back — and the next one should
          // not need the nav rearranged around it.
          { path: "/utilities", text: t("verticalParallel.menu") },
        ]
      : []),
  ]
  const activeNavPath = getActiveNavPath(pathname, navLinks)

  return (
    <header className="flex h-[50px] w-full min-w-0 items-center justify-between gap-x-1 border-b border-border-color text-default md:gap-x-[10px]">
      <section className="flex min-w-0 items-center">
        {showPreviousButton ? (
          <button
            onClick={(e) => {
              e.preventDefault()
              router.history.back()
            }}
            className="p-3"
          >
            <section className="flex cursor-pointer items-center justify-center gap-2">
              <LeftArrowIcon
                className="text-gray-backdrop-900"
                fill="currentColor"
              />
              <div className="text-sm font-normal">{t("plan.backButton")}</div>
            </section>
          </button>
        ) : (
          <>
            {!hideNavigationTab && (
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="iconLg"
                    className="ml-1 shrink-0 xl:hidden"
                    aria-label={t("common.openNavigationMenu")}
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="left"
                  className="w-[280px] max-w-[85vw] gap-0 overflow-y-auto p-0"
                >
                  <SheetTitle className="sr-only">BeringAI</SheetTitle>
                  <SheetDescription className="sr-only">
                    {t("common.navigationDescription")}
                  </SheetDescription>
                  <div className="flex h-[50px] items-center gap-2 border-b border-border-color px-4 text-key-color">
                    <Logo fill="currentColor" width="24" height="24" />
                    <span className="text-lg font-bold">BeringAI</span>
                  </div>
                  <nav className="flex flex-col py-2">
                    {navLinks.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        search={item.search}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={cn(
                          "border-l-2 px-4 py-3 text-sm",
                          activeNavPath === item.path
                            ? "border-key-color bg-key-color/5 font-bold text-key-color"
                            : "border-transparent text-gray-700"
                        )}
                      >
                        <span className="flex items-center gap-1.5">
                          {item.text}
                          {item.path === "/assistant" && <QualityEvaluationBadge />}
                        </span>
                      </Link>
                    ))}
                  </nav>
                  <div className="border-y border-border-color px-4 py-3">
                    <p className="mb-2 text-xs font-medium text-gray-backdrop-900">
                      {t("common.language")}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {i18nNavigation.map((item) => (
                        <Button
                          key={item.value}
                          variant="outline"
                          size="sm"
                          className={cn(
                            "justify-center",
                            (item.value === i18n.language ||
                              (item.value === UserLanguageEnum.Ko &&
                                isKoreanLanguage) ||
                              (item.value === UserLanguageEnum.En &&
                                isEnglishLanguage)) &&
                              "border-key-color bg-key-color/5 text-key-color"
                          )}
                          onClick={() => {
                            handleChangeLanguage(item.value)
                            i18n.changeLanguage(
                              convertToI18nLanguage(item.value)
                            )
                            setIsMobileMenuOpen(false)
                          }}
                        >
                          {item.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                  {!hideProfile && (
                    <div className="mt-auto border-t border-border-color pb-2">
                      <div className="flex items-center gap-2 overflow-hidden px-4 py-3">
                        <UserProfileIcon
                          width="28"
                          height="28"
                          className="shrink-0"
                        />
                        <section className="min-w-0">
                          <p className="truncate text-sm font-medium">
                            {user.userName}
                          </p>
                          <p className="truncate text-xs text-gray-backdrop-900">
                            {user.email}
                          </p>
                        </section>
                      </div>
                      {!hidePlanButton && (
                        <Link
                          to="/subscription"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700"
                        >
                          {isUltimatePlan
                            ? t("common.managePlanButton")
                            : t("common.headerButton")}
                        </Link>
                      )}
                      {userNavigation.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700"
                        >
                          {item.name}
                        </Link>
                      ))}
                      <button
                        type="button"
                        className="block w-full px-4 py-2 text-left text-sm text-gray-700"
                        onClick={() => {
                          setIsMobileMenuOpen(false)
                          logout()
                        }}
                      >
                        {t("settings.logout")}
                      </button>
                    </div>
                  )}
                </SheetContent>
              </Sheet>
            )}
            <Link to="/" className="min-w-0 p-2 md:p-3">
              <section className="mr-0 flex min-w-0 items-center gap-x-2 text-key-color xl:mr-4 md:gap-x-[10px]">
                <Logo fill="currentColor" width="24" height="24" />
                <span className="truncate text-lg font-bold md:text-xl">
                  BeringAI
                </span>
              </section>
            </Link>
            {!hideNavigationTab && (
              <section className="hidden shrink-0 items-center gap-x-2 whitespace-nowrap text-sm xl:flex">
                {navLinks.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    search={item.search}
                    className={cn(
                      "flex h-full items-center border-b-2 px-2 py-3",
                      activeNavPath === item.path
                        ? "border-key-color text-key-color"
                        : "border-transparent"
                    )}
                  >
                    <p
                      className={
                        cn(
                          "flex items-center gap-1.5",
                          activeNavPath === item.path && "font-bold"
                        )
                      }
                    >
                      {item.text}
                      {item.path === "/assistant" && <QualityEvaluationBadge />}
                    </p>
                  </Link>
                ))}
              </section>
            )}
          </>
        )}
      </section>
      <section className="flex shrink-0 items-center gap-x-1 pr-2 md:gap-x-3 md:pr-3">
        <Popover open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="plain"
              size="iconLg"
              aria-label={t("common.language")}
              className={cn(
                !showPreviousButton &&
                  !hideNavigationTab &&
                  "hidden xl:inline-flex",
                isDropdownOpen
                  ? "bg-gray-backdrop-300"
                  : "hover:bg-outline-button-hovered"
              )}
            >
              <EarthIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="mr-3 w-[120px]">
            {i18nNavigation.map((item) => (
              <button
                className={cn(
                  item.value === i18n.language
                    ? "cursor-pointer bg-gray-100"
                    : "",
                  "w-full border-none px-4 py-2 text-sm text-gray-700 hover:bg-gray-50",
                  (item.value === UserLanguageEnum.Ko || isKoreanLanguage) &&
                    "rounded-t-default hover:rounded-t-default",
                  (item.value === UserLanguageEnum.En ||
                    i18n.language === UserLanguageEnum.En) &&
                    "rounded-b-default hover:rounded-b-default"
                )}
                key={item.value}
                onClick={() => {
                  handleChangeLanguage(item.value)
                  i18n.changeLanguage(convertToI18nLanguage(item.value))
                  setIsDropdownOpen(false)
                }}
              >
                {item.name}
              </button>
            ))}
          </PopoverContent>
        </Popover>
        <ThemeToggle />
        <NotificationPopover />
        {isUsageInfoPending ? (
          <span className="hidden xl:inline-flex">
            <Spinner />
          </span>
        ) : isOnTranslationsPage && !isUnlimitedUser ? (
          <section className="hidden shrink-0 items-center space-x-2 whitespace-nowrap rounded-default border border-border-color p-1.5 2xl:flex">
            <p className="rounded-default bg-gray-color-2 px-1.5 text-sm">
              {getTierName(quota) + " plan"}
            </p>
            <UsageDisplay
              accumulatedCount={Number(accumulatedCount) || 0}
              quota={quota}
              percent={percent}
              isFreeTierUser={!contractInfo}
            />
            {isOverEighty && !isOverused && <UsageWarningTooltip />}
            {isOverused && <OveruseTooltip isFreeUser={!contractInfo} />}
          </section>
        ) : null}
        {!hideProfile && (
          <section className="flex items-center gap-x-3">
            {!hidePlanButton && (
              <Button variant="black" className="hidden xl:inline-flex" asChild>
                <Link to="/subscription">
                  {isUltimatePlan
                    ? t("common.managePlanButton")
                    : t("common.headerButton")}
                </Link>
              </Button>
            )}
            <WorkspaceSwitcher />
            <Popover open={isProfileOpen} onOpenChange={setIsProfileOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="iconLg"
                  className={`${isProfileOpen ? "bg-gray-backdrop-300" : "hover:bg-outline-button-hovered"}`}
                  aria-label={t("settings.profile2")}
                >
                  <UserProfileIcon width="20" height="20" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="mr-3 w-[240px]">
                <div className="mb-3 mt-2 flex items-center space-x-2 overflow-hidden">
                  <UserProfileIcon
                    width="32"
                    height="32"
                    className="ml-4 shrink-0"
                  />
                  <section className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {user.userName}
                    </div>
                    <div className="truncate text-xs text-gray-backdrop-900">
                      {user.email}
                    </div>
                  </section>
                </div>
                {isOnTranslationsPage && !isUnlimitedUser && (
                  <section className="mx-3 mb-3 space-y-2 rounded-default border border-border-color p-3 2xl:hidden">
                    <p className="text-xs font-medium">{getTierName(quota)} plan</p>
                    <UsageDisplay
                      accumulatedCount={used}
                      quota={quota}
                      percent={percent}
                      isFreeTierUser={!contractInfo}
                      variant="dialog"
                    />
                    {isOverEighty && !isOverused && <UsageWarningTooltip />}
                    {isOverused && <OveruseTooltip isFreeUser={!contractInfo} />}
                  </section>
                )}
                <div className="flex flex-col border-y border-border-color">
                  {userNavigation.map(
                    (item: { name: string; href: string }) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="block px-4 py-2 text-sm text-gray-700"
                      >
                        {item.name}
                      </Link>
                    )
                  )}
                  <Link
                    onClick={logout}
                    className="block px-4 py-2 text-sm text-gray-700"
                  >
                    {t("settings.logout")}
                  </Link>
                </div>
              </PopoverContent>
            </Popover>
          </section>
        )}
        {typeof handleApiButton === "function" && (
          <Button variant="black" onClick={handleApiButton}>
            {isApiMode
              ? t("settings.subscribeStandardPlan")
              : t("settings.subscribeApiPlan")}
          </Button>
        )}
      </section>
    </header>
  )
}

export default GlobalNavBar
